import { getFieldCategory } from './fieldCategoryMapping';
import { FIELD_CATEGORIES } from './fieldCategoryUtils';
import { logger } from './logger';

/**
 * Maps customer data to workflow fields based on field names and categories
 * This function intelligently maps customer profile data to corresponding workflow fields
 * 
 * @param {Object} customerData - The customer data object
 * @param {Array} workflowFields - Array of workflow field objects
 * @returns {Object} - Mapped field values keyed by field name
 */
export const mapCustomerDataToWorkflowFields = (customerData, workflowFields) => {
  if (!customerData || !workflowFields || !Array.isArray(workflowFields)) {
    logger.warn('Invalid inputs for customer data mapping', { 
      hasCustomerData: !!customerData, 
      hasWorkflowFields: !!workflowFields,
      isWorkflowFieldsArray: Array.isArray(workflowFields)
    });
    return {};
  }

  logger.debug('Mapping customer data to workflow fields', { 
    customerDataKeys: Object.keys(customerData),
    workflowFieldCount: workflowFields.length
  });

  const mappedValues = {};

  // Define field mapping rules
  const fieldMappings = {
    // Personal information fields
    firstName: ['firstname', 'first_name', 'firstnameen', 'first_name_en'],
    lastName: ['lastname', 'last_name', 'lastnameen', 'last_name_en'],
    fullName: ['fullname', 'full_name', 'name', 'nameen', 'name_en', 'fullnameen'],
    email: ['email', 'emailaddress', 'email_address'],
    phone: ['phone', 'phonenumber', 'phone_number', 'mobile', 'mobilenumber', 'mobile_number'],
    dateOfBirth: ['dateofbirth', 'dob', 'birthdate', 'birth_date'],
    gender: ['gender', 'sex'],
    nationality: ['nationality', 'nation', 'country'],
    
    // Address fields
    address: ['address', 'homeaddress', 'home_address', 'residentialaddress', 'residential_address'],
    city: ['city', 'town'],
    state: ['state', 'province', 'region'],
    country: ['country', 'nation'],
    postalCode: ['postalcode', 'postal_code', 'zipcode', 'zip_code', 'zip'],
    
    // ID document fields
    passportNumber: ['passport', 'passportnumber', 'passport_number', 'passportno', 'passport_no'],
    idNumber: ['idnumber', 'id_number', 'nationalid', 'national_id', 'emiratesid', 'emirates_id'],
    
    // Employment fields
    company: ['company', 'employer', 'organization', 'organisation', 'companyname', 'company_name'],
    position: ['position', 'title', 'jobtitle', 'job_title'],
    department: ['department', 'dept'],
    
    // Other common fields
    reference: ['reference', 'referencenumber', 'reference_number', 'ref', 'refnumber', 'ref_number']
  };

  // Process each workflow field
  workflowFields.forEach(field => {
    if (!field || !field.name) return;
    
    const fieldName = field.name.toLowerCase();
    const fieldTitle = (field.title || '').toLowerCase();
    const fieldCode = (field.code || '').toLowerCase();
    
    // Skip document fields as they need special handling
    if (field.type === 'FILE' || field.type === 'DOCUMENT' || field.type === 'PDF' || field.type === 'IMAGE') {
      return;
    }
    
    // Try to find a match from our mapping rules
    let customerValue = null;
    let matchFound = false;
    
    // Check each mapping rule
    Object.entries(fieldMappings).forEach(([customerField, mappedFields]) => {
      if (matchFound) return; // Skip if we already found a match
      
      // Check if field name/title/code matches any of the mapped fields
      if (mappedFields.some(mappedField => 
          fieldName.includes(mappedField) || 
          fieldTitle.includes(mappedField) || 
          fieldCode.includes(mappedField))) {
        
        // If we have this field in customer data, use it
        if (customerData[customerField] !== undefined) {
          customerValue = customerData[customerField];
          matchFound = true;
          logger.debug(`Mapped customer field ${customerField} to workflow field ${field.name}`);
        }
      }
    });
    
    // If no match found by name mapping, try category-based mapping
    if (!matchFound) {
      const fieldCategory = getFieldCategory(field.name);
      
      switch (fieldCategory) {
        case FIELD_CATEGORIES.PERSONAL:
          // Try direct field name match for personal fields
          if (customerData[fieldName]) {
            customerValue = customerData[fieldName];
            matchFound = true;
          } else if (customerData[fieldTitle]) {
            customerValue = customerData[fieldTitle];
            matchFound = true;
          } else if (customerData[fieldCode]) {
            customerValue = customerData[fieldCode];
            matchFound = true;
          }
          break;
          
        case FIELD_CATEGORIES.CONTACT:
          // For contact fields, try some common variations
          if (fieldName.includes('email') && customerData.email) {
            customerValue = customerData.email;
            matchFound = true;
          } else if ((fieldName.includes('phone') || fieldName.includes('mobile')) && customerData.phone) {
            customerValue = customerData.phone;
            matchFound = true;
          }
          break;
          
        case FIELD_CATEGORIES.ADDRESS:
          // For address fields, check address components
          if (fieldName.includes('address') && customerData.address) {
            customerValue = customerData.address;
            matchFound = true;
          } else if (fieldName.includes('city') && customerData.city) {
            customerValue = customerData.city;
            matchFound = true;
          } else if ((fieldName.includes('country') || fieldName.includes('nation')) && customerData.country) {
            customerValue = customerData.country;
            matchFound = true;
          }
          break;
          
        default:
          // No special handling for other categories
          break;
      }
    }
    
    // If we found a matching value, add it to our results
    if (matchFound && customerValue !== null) {
      mappedValues[field.name] = customerValue;
    }
  });
  
  logger.debug('Customer data mapping complete', { 
    mappedFieldCount: Object.keys(mappedValues).length,
    mappedFields: Object.keys(mappedValues)
  });
  
  return mappedValues;
};

/**
 * Enhanced customer UID validation with detailed error handling
 * 
 * @param {string} uid - The customer UID to validate
 * @param {Function} apiCallback - Function to call API for validation
 * @returns {Promise<Object>} - Validation result with status and details
 */
export const validateCustomerUid = async (uid, apiCallback) => {
  if (!uid) {
    return {
      isValid: false,
      error: 'Customer UID is required',
      details: null
    };
  }
  
  // Basic format validation
  const uidPattern = /^[A-Za-z0-9-]+$/;
  if (!uidPattern.test(uid)) {
    return {
      isValid: false,
      error: 'Invalid UID format. UIDs should only contain letters, numbers, and hyphens.',
      details: null
    };
  }
  
  try {
    logger.debug('Validating customer UID', { uid });
    
    // Call the API to validate the UID
    const response = await apiCallback(uid);
    
    if (!response || !response.data) {
      return {
        isValid: false,
        error: 'Customer not found with the provided UID',
        details: null
      };
    }
    
    const customerData = response.data;
    
    // Format customer info for display
    const customerInfo = {
      name: customerData.nameEn || `${customerData.firstNameEn || ''} ${customerData.lastNameEn || ''}`.trim(),
      organization: customerData.organizationName || 'N/A',
      email: customerData.email || 'N/A',
      phone: customerData.mobileNumber || customerData.phoneNumber || 'N/A',
      uid: customerData.uid
    };
    
    logger.debug('Customer UID validated successfully', { customerInfo });
    
    return {
      isValid: true,
      error: null,
      details: {
        customerInfo,
        customerData
      }
    };
  } catch (error) {
    logger.error('Error validating customer UID', { uid, error });
    
    // Provide more specific error messages based on error type
    if (error.response) {
      if (error.response.status === 404) {
        return {
          isValid: false,
          error: 'Customer not found with the provided UID',
          details: null
        };
      } else if (error.response.status === 401 || error.response.status === 403) {
        return {
          isValid: false,
          error: 'You do not have permission to access this customer information',
          details: null
        };
      }
    }
    
    return {
      isValid: false,
      error: 'An error occurred while validating the customer UID',
      details: null
    };
  }
};

/**
 * Maps document fields from customer data to workflow fields
 * 
 * @param {Object} customerData - The customer data object
 * @param {Array} documentFields - Array of document field objects from workflow
 * @returns {Object} - Mapped document values keyed by field name
 */
export const mapCustomerDocumentsToWorkflowFields = (customerData, documentFields) => {
  if (!customerData || !documentFields || !Array.isArray(documentFields)) {
    return {};
  }
  
  const mappedDocuments = {};
  
  // Common document field mappings
  const documentMappings = {
    passport: ['passport', 'passportcopy', 'passport_copy'],
    idCard: ['idcard', 'id_card', 'nationalid', 'national_id', 'emiratesid', 'emirates_id'],
    visa: ['visa', 'visacopy', 'visa_copy', 'visapage', 'visa_page'],
    photo: ['photo', 'picture', 'profilepicture', 'profile_picture', 'photograph'],
    contract: ['contract', 'employmentcontract', 'employment_contract', 'labourcontract', 'labour_contract'],
    certificate: ['certificate', 'certificates', 'educationcertificate', 'education_certificate']
  };
  
  // Check if customer has documents
  const customerDocuments = customerData.documents || [];
  
  if (customerDocuments.length === 0) {
    logger.debug('No customer documents found for mapping');
    return mappedDocuments;
  }
  
  // Process each document field in the workflow
  documentFields.forEach(field => {
    if (!field || !field.name) return;
    
    const fieldName = field.name.toLowerCase();
    const fieldTitle = (field.title || '').toLowerCase();
    
    // Try to find a matching document from customer data
    let matchedDocument = null;
    
    // Check each document mapping
    Object.entries(documentMappings).forEach(([docType, mappedFields]) => {
      if (matchedDocument) return; // Skip if already matched
      
      // Check if field name/title matches any of the mapped document types
      if (mappedFields.some(mappedField => 
          fieldName.includes(mappedField) || 
          fieldTitle.includes(mappedField))) {
        
        // Find a matching document in customer documents
        const document = customerDocuments.find(doc => 
          doc.documentType && doc.documentType.toLowerCase() === docType.toLowerCase()
        );
        
        if (document && document.url) {
          matchedDocument = document;
          logger.debug(`Mapped customer document ${docType} to workflow field ${field.name}`);
        }
      }
    });
    
    // If we found a matching document, add it to our results
    if (matchedDocument) {
      mappedDocuments[field.name] = {
        url: matchedDocument.url,
        name: matchedDocument.name || `${field.title || field.name} Document`,
        type: matchedDocument.contentType || 'application/octet-stream',
        size: matchedDocument.size || 0,
        uploadDate: matchedDocument.uploadDate || new Date().toISOString()
      };
    }
  });
  
  logger.debug('Customer document mapping complete', { 
    mappedDocumentCount: Object.keys(mappedDocuments).length,
    mappedDocumentFields: Object.keys(mappedDocuments)
  });
  
  return mappedDocuments;
};

/**
 * Formats customer data for display in workflow UI
 * 
 * @param {Object} customerData - The customer data object
 * @returns {Object} - Formatted customer display data
 */
export const formatCustomerDisplayData = (customerData) => {
  if (!customerData) {
    return null;
  }
  
  // Extract name components with fallbacks
  const firstName = customerData.firstNameEn || customerData.firstName || '';
  const lastName = customerData.lastNameEn || customerData.lastName || '';
  const fullName = customerData.nameEn || customerData.name || `${firstName} ${lastName}`.trim();
  
  return {
    name: fullName || 'N/A',
    organization: customerData.organizationName || customerData.organization || 'N/A',
    email: customerData.email || 'N/A',
    phone: customerData.mobileNumber || customerData.phoneNumber || customerData.phone || 'N/A',
    uid: customerData.uid || 'N/A',
    address: customerData.address || 'N/A',
    documentCount: (customerData.documents || []).length
  };
};
